import React, { SyntheticEvent, useState } from 'react';
import { Autocomplete, Chip, FormHelperText, TextField } from '@mui/material';
import { Spacer } from '../new/Spacer';
import CloseIcon from '@mui/icons-material/Close';

interface BkChipAutocompleteProps {
  title: string;
  tags: string[];
  setTags: (key: 'organizationDomains' | 'usersToInvite', newTags: string[]) => void;
  onValidate?: (value: string) => string | null; // Optional validation
  errorOnSubmit?: string;
  type: 'organizationDomains' | 'usersToInvite';
  error?: string;
}

const DELIMETER_KEYS = [' ', 'Enter', ';', ','];

const BkChipAutocomplete: React.FC<BkChipAutocompleteProps> = ({
  title,
  tags,
  setTags,
  onValidate,
  errorOnSubmit,
  type,
  error,
}) => {
  const [currentInput, setCurrentInput] = useState<string>('');
  const [internalError, setError] = useState<string>(error || '');

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (!trimmedTag) return;

    if (tags.includes(trimmedTag)) {
      setError(`Cannot add the same ${title} twice`);
      return;
    }

    if (onValidate) {
      const validationError = onValidate(trimmedTag);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    setTags(type, [...tags, trimmedTag]); // Add the new tag
    setCurrentInput(''); // Clear the input field
    setError(''); // Clear any error
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (DELIMETER_KEYS.includes(event.key) && currentInput.trim() !== '') {
      event.preventDefault();
      addTag(currentInput);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentInput(event.target.value);
    setError(''); // Clear error while typing
  };

  const handleBlur = () => {
    if (currentInput.trim() !== '') {
      addTag(currentInput);
    }
  };

  return (
    <div>
      <Autocomplete
        clearIcon={false}
        options={[]} // No suggestions for now
        freeSolo
        multiple
        value={tags}
        onChange={(event: SyntheticEvent & { key?: string }, newValue) => {
          if (event.key === 'Enter' && onValidate && onValidate(newValue[newValue.length]) !== null) {
            return; //otherwise invalid value will be added when clicking enter
          }
          setTags(type, newValue as string[]);
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...props } = getTagProps({ index });
            return (
              <Chip
                sx={{ border: '0.0625rem solid black', backgroundColor: 'transparent', borderRadius: '0.5rem' }}
                label={option}
                deleteIcon={<CloseIcon sx={{ color: '#45464f !important', fontSize: '1.125rem !important' }} />}
                key={key}
                {...props}
              />
            );
          })
        }
        clearOnBlur={true}
        sx={{
          marginTop: '0.3125rem',
          '& .MuiOutlinedInput-notchedOutline': {
            border: '0.0625rem solid #7E828B',
            borderRadius: '0.375rem',
            fontSize: '1rem',
            fontWeight: 400,
            fontFamily: 'Instrument Sans',
            lineHeight: '1.5rem',
            padding: '0.75rem 1rem',
          },
        }}
        renderInput={(params) => (
          <>
            <TextField
              {...params}
              value={currentInput} // Synchronize value with currentInput
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              // error={!!error || !!errorOnSubmit || !!internalError}
              InputProps={{
                ...params.InputProps,
                value: currentInput, // Explicitly sync the input value
              }}
            />
            {error ? (
              <FormHelperText
                sx={{
                  color: '#AC3737',
                  marginTop: '0.375rem',
                  fontFamily: 'Instrument Sans',
                  fontSize: '0.875rem',
                  lineHeight: '1rem',
                  height: '1.5rem',
                }}
              >
                {error || internalError || errorOnSubmit}
              </FormHelperText>
            ) : (
              <Spacer h={24} />
            )}
          </>
        )}
      />
    </div>
  );
};

export default BkChipAutocomplete;
